import React, {useEffect, useState} from "react";
import {useTranslation} from "next-i18next";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {
  fetchOnboardingData,
  reset,
  selectError,
  selectIsLoading,
  selectOnboardingData
} from "../../redux/onboarding/onboardingSlice";
import {Heading4, HeroHeading2} from "../text";
import {QRCodeSVG} from 'qrcode.react';
import tailwindConfig from "../../tailwind.config";
import {Button} from "../buttons/Button";
import {AndroidFilledIcon, IosFilledIcon} from "../icons";
import Skeleton from "react-loading-skeleton";
import Link from "next/link"

export const OnboardingView: React.FC<any> = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const onboardingData = useAppSelector(selectOnboardingData)
  const error = useAppSelector(selectError)
  const isLoading = useAppSelector(selectIsLoading)

  const url = onboardingData?.url

  const isAndroid = navigator.userAgent.indexOf( "Android" ) !== -1
  const isIphone = navigator.userAgent.indexOf( "iPhone" ) !== -1

  const [iOSButtonUrl, setIOSButtonUrl] = useState<string>()
  const [androidButtonUrl, setAndroidButtonUrl] = useState<string>()

  useEffect(() => {
    if (url && (isIphone || isAndroid)) {
      setIOSButtonUrl(url)
      setAndroidButtonUrl(url)
    } else if (isIphone || isAndroid) {
      setIOSButtonUrl(process.env.NEXT_PUBLIC_APP_DOWNLOAD_URL)
      setAndroidButtonUrl(process.env.NEXT_PUBLIC_APP_DOWNLOAD_URL)
    } else {
      setIOSButtonUrl(process.env.NEXT_PUBLIC_FITGENIE_APP_STORE_URL)
      setAndroidButtonUrl(process.env.NEXT_PUBLIC_FITGENIE_PLAY_STORE_URL)
    }
  }, [isAndroid, isIphone, url])

  useEffect(() => {
    dispatch(reset())
    dispatch(fetchOnboardingData())
  }, [dispatch])

  return <div className="md:max-w-lg md:rounded-xl md:border-2 md:border-fg-gray-surface md:py-8 px-4 md:px-8 mx-auto mt-10">
    <div className={`flex flex-col items-center space-y-4 w-full`}>
      <HeroHeading2 className="text-center w-full">
        {isLoading
          ? <div className="flex flex-col space-y-2"><Skeleton className="!w-1/2" /><Skeleton className="!w-3/4" /></div>
          : <span>Get the App to Begin <span className="font-bold text-fg-primary">Your Macro Plan!</span></span>
        }
      </HeroHeading2>

      <div className="h-6" />

      <Heading4 className={`text-center w-full ${isIphone || isAndroid ? 'hidden' : ''}`}>{ isLoading ? <Skeleton className="!w-1/2" /> : 'Scan Your QR Code'}</Heading4>

      {
        (() => {
          if (isLoading && !isIphone && !isAndroid) {
            return <Skeleton className="!rounded-xl" style={{ height: 256, width: 256 }} />
          } else if (url && !isIphone && !isAndroid) {
            return <QRCodeSVG
              value={url}
              size={256}
              level="L"
              bgColor={tailwindConfig.theme.colors['white']}
              fgColor={tailwindConfig.theme.colors['fg-black']}
              includeMargin={false}
            />
          } else {
            return <div className="relative w-full h-screen-1/2">
              <img
                src="../images/upgrade_graphic_recipe.webp"
                className={`absolute inset-x-2/3 inset-y-1/2 transform -translate-y-1/2 -translate-x-1/2 drop-shadow-2xl transition-opacity max-h-screen-1/2 object-contain lg:w-2/3`}
              />

              <img
                src="../images/upgrade_graphic_weekly_update.webp"
                className={`absolute inset-x-1/3 inset-y-1/2 transform -translate-y-1/2 mt-6 -translate-x-1/2 drop-shadow-2xl transition-opacity max-h-screen-1/2 object-contain lg:w-2/3`}
              />
            </div>
          }
        })()
      }

      <div className={`h-12 ${isIphone || isAndroid ? '' : 'hidden'}`} />

      <div className={`w-full ${isIphone || isAndroid ? '' : 'hidden'}`}>
        <a target="_blank" rel="noreferrer" href={url}>
          <Button size="lg" isSkeletonLoading={isLoading} isFullWidth={true}>Begin Your Macro Plan</Button>
        </a>
      </div>

      <div className={`flex flex-row space-x-2 justify-items-start ${isIphone || isAndroid ? 'hidden' : ''}`}>
        <a target="_blank" rel="noreferrer" href={iOSButtonUrl}>
          <Button className={`${isAndroid ? 'hidden' : ''}`} isSkeletonLoading={isLoading} buttonStyle="secondary" leadingicon={<IosFilledIcon />}>
            {t('common:nav_title_iphone')}
          </Button>
        </a>
        <a target="_blank" rel="noreferrer" href={androidButtonUrl}>
          <Button className={`${isIphone ? 'hidden' : ''}`} isSkeletonLoading={isLoading} buttonStyle="secondary" leadingicon={<AndroidFilledIcon />}>
            {t('common:nav_title_android')}
          </Button>
        </a>
      </div>
    </div>
  </div>
}